import React, { useEffect, useState, useMemo } from "react";
import dayjs from "dayjs";
import DOMPurify from "dompurify";

import AdPieChart, { AdBreakdown } from "../components/AdPieChart";
import KpiCards, { KpiSnapshot } from "../components/KpiCards";
import MonthlyChart, { MonthlySummary } from "../components/MonthlyChart";
import { KpiVisibilitySettings } from "../components/KpiVisibilitySettings";

import { apiGet } from "../utils/apiClient";
import { useUserContext } from "../contexts/UserContext";
import { useMallContext } from "../contexts/MallContext";
import LoadingInline from "../components/LoadingInline";
import { KPI_LIST } from "../constants/kpi";
import { isFreePlanUser } from "../utils/plan";

import MonthlySalesProfitYoYChart, {
    MonthlySalesProfitYoYRow,
} from "../components/MonthlySalesProfitYoYChart";

import PromotionCostBreakdownYoYChart, {
    PromotionCostBreakdownYoYRow,
} from "../components/PromotionCostBreakdownYoYChart";

import ProfitStructureYoYChart, {
    ProfitStructureBreakdownYoYRow,
} from "../components/ProfitStructureYoYChart";

import AdCostBreakdownYoYChart, {
    AdCostBreakdownYoYRow,
} from "../components/AdCostBreakdownYoYChart";

import { HelpTip } from "../components/HelpTip";
import { SetupBanner, ZoomBanner } from "../components/SetupBanners";

/* --------------------------------------------
 * 型定義
 * -------------------------------------------- */

export interface FullKpiSnapshot extends KpiSnapshot {
    year_month: string;
    period_start: string;
    period_end: string;

    rpp_amount_spent?: number;
    rppexp_amount_spent?: number;
    cpa_amount_spent?: number;
    cpnadv_amount_spent?: number;
    ec_amount_spent?: number;
    tda_amount_spent?: number;
    tdaexp_amount_spent?: number;
    deal_amount_spent?: number;

    visit?: number;

    cost?: number;
    commission?: number;
    logistics?: number;
    fixed_cost?: number;

    operating_profit_after_fixed?: number;
    operating_profit_after_fixed_rate?: number;
}

interface MonthlyKpiResponse {
    anchor_day: number;
    current?: FullKpiSnapshot;
    compare?: FullKpiSnapshot;
    diff?: any;
}

interface MonthlyChartResponse {
    anchor_day: number;
    chart24?: FullKpiSnapshot[];
}

interface MonthlyPlResponse {
    fiscalMonth: number;
    data: any[];
}

// 互換（demo / 旧レスポンスも飲めるように）
interface MonthlyFullResponse extends MonthlyKpiResponse, MonthlyChartResponse {
    lastYear?: FullKpiSnapshot;
}

/* ★ お知らせ（HTML対応） */
type Notice = {
    id: string;
    title: string;
    body?: string;
    bodyHtml?: string;
    level: "info" | "warning" | "critical";
};

/* --------------------------------------------
 * お知らせ用メタ
 * -------------------------------------------- */

function noticeMeta(level: Notice["level"]) {
    if (level === "critical") return { badge: "bg-red-500", label: "重要" };
    if (level === "warning") return { badge: "bg-yellow-400", label: "注意" };
    return { badge: "bg-blue-500", label: "お知らせ" };
}

/* --------------------------------------------
 * 広告内訳
 * -------------------------------------------- */

const AD_FIELD_MAP = [
    { key: "rpp_amount_spent", label: "検索連動型RPP" },
    { key: "cpa_amount_spent", label: "楽天CPA広告" },
    { key: "ec_amount_spent", label: "楽天グループ" },
    { key: "rppexp_amount_spent", label: "エクスパンション" },
    { key: "cpnadv_amount_spent", label: "運用型クーポン" },
];

function toAdPieData(row?: FullKpiSnapshot | null): AdBreakdown[] {
    if (!row) return [];
    return AD_FIELD_MAP.map(({ key, label }) => ({
        name: label,
        value: Number((row as any)[key] ?? 0) || 0,
    }));
}

/* --------------------------------------------
 * compareYm 初期値
 * -------------------------------------------- */

function calcInitialCompareYm(): string {
    const today = dayjs();
    const base = dayjs(today.format("YYYY-MM") + "-01");
    return today.date() === 1
        ? base.subtract(2, "month").format("YYYY-MM")
        : base.subtract(1, "month").format("YYYY-MM");
}

/* --------------------------------------------
 * DashboardPage
 * -------------------------------------------- */

const STORAGE_KEY = "kpiCards.visible.v1";

const InlineSpinner: React.FC = () => (
    <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-slate-300 border-t-transparent" />
);

async function fetchJson<T>(url: string) {
    const res = await apiGet(url);
    if (!res.ok) throw new Error(`api failed: ${res.status}`);
    return (await res.json()) as T;
}

const DashboardPage: React.FC = () => {
    const user = useUserContext();
    const { mall } = useMallContext();
    const isAmazonMall = mall === "amazon";
    const isYahooMall = mall === "yahoo";
    // 楽天固有のKPI・広告内訳を出さないモール（Amazon / Yahoo 共通挙動）
    const isNonRakutenMall = isAmazonMall || isYahooMall;
    const isDemo =
        typeof window !== "undefined" && window.location.pathname.startsWith("/demo");

    const [adShowCompare, setAdShowCompare] = useState(false);
    const [compareYm, setCompareYm] = useState(calcInitialCompareYm);

    // ★分割ロード：KPI と Chart を別stateで管理
    const [kpiData, setKpiData] = useState<MonthlyKpiResponse | null>(null);
    const [chartDataRes, setChartDataRes] = useState<MonthlyChartResponse | null>(null);
    const [monthlyPlRes, setMonthlyPlRes] = useState<MonthlyPlResponse | null>(null);

    const [loadingKpi, setLoadingKpi] = useState(true);
    const [loadingChart, setLoadingChart] = useState(true);
    const [loadingMonthlyPl, setLoadingMonthlyPl] = useState(true);

    // ★ chartが既に取得済みか（stale closure 対策）
    const chartLoadedRef = React.useRef(false);
    useEffect(() => {
        chartLoadedRef.current = !!(chartDataRes?.chart24 && chartDataRes.chart24.length > 0);
    }, [chartDataRes]);

    // ★ request競合（compareYm連打など）対策：最新のみ反映
    const reqSeqRef = React.useRef(0);

    const [notices, setNotices] = useState<Notice[]>([]);
    const [noticesLoading, setNoticesLoading] = useState(true);
    const [noticesError, setNoticesError] = useState<string | null>(null);

    const [visibleKpis, setVisibleKpis] = useState<Record<string, boolean>>({});

    // メルマガ費用・アフィリエイトは楽天固有で Amazon では常に0のためカードを出さない
    const visibleKpisForMall = useMemo(
        () =>
            isNonRakutenMall
                ? { ...visibleKpis, afl_amount_spent: false, mail_amount_spent: false }
                : visibleKpis,
        [isNonRakutenMall, visibleKpis],
    );

    const isSuperUser = Boolean(
        (user as any)?.superAdmin ||
        (user as any)?.isSuperAdmin ||
        (user as any)?.role === "super"
    );

    // =========================
    // ★ Free plan: 月の切り替えブロック
    // =========================
    const isFreePlan = useMemo(() => isFreePlanUser(user), [user]);
    const [planBlockMsg, setPlanBlockMsg] = useState<string | null>(null);
    const planBlockTimer = React.useRef<number | null>(null);

    const showPlanBlocked = React.useCallback(() => {
        setPlanBlockMsg("フリープランのため年月の切り替えは利用できません。");
        if (planBlockTimer.current) window.clearTimeout(planBlockTimer.current);
        planBlockTimer.current = window.setTimeout(() => setPlanBlockMsg(null), 2500);
    }, []);

    const PROFIT_STRUCT_COMPARE_KEY = "profitStructure.compare.v1";
    const [profitStructShowCompare, setProfitStructShowCompare] = useState(() => {
        try {
            return sessionStorage.getItem(PROFIT_STRUCT_COMPARE_KEY) === "1";
        } catch {
            return false;
        }
    });
    useEffect(() => {
        try {
            sessionStorage.setItem(PROFIT_STRUCT_COMPARE_KEY, profitStructShowCompare ? "1" : "0");
        } catch { }
    }, [profitStructShowCompare]);

    const PROMO_COMPARE_KEY = "promoBreakdown.compare.v1";
    const [promoShowCompare, setPromoShowCompare] = useState(() => {
        try {
            return sessionStorage.getItem(PROMO_COMPARE_KEY) === "1";
        } catch {
            return false;
        }
    });
    useEffect(() => {
        try {
            sessionStorage.setItem(PROMO_COMPARE_KEY, promoShowCompare ? "1" : "0");
        } catch { }
    }, [promoShowCompare]);

    useEffect(() => {
        return () => {
            if (planBlockTimer.current) window.clearTimeout(planBlockTimer.current);
        };
    }, []);

    /* KPI表示状態（読み込み） */
    useEffect(() => {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (raw) {
            try {
                setVisibleKpis(JSON.parse(raw));
            } catch {
                const defaults: Record<string, boolean> = {};
                KPI_LIST.forEach((k) => (defaults[k.key] = true));
                setVisibleKpis(defaults);
            }
        } else {
            const defaults: Record<string, boolean> = {};
            KPI_LIST.forEach((k) => (defaults[k.key] = true));
            setVisibleKpis(defaults);
        }
    }, []);

    /* KPI表示状態（保存） */
    useEffect(() => {
        if (!visibleKpis || Object.keys(visibleKpis).length === 0) return;
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(visibleKpis));
    }, [visibleKpis]);

    /* お知らせ取得（先行） */
    useEffect(() => {
        if (user.loading) return;

        if (!isDemo && !user.companyId) {
            setNotices([]);
            setNoticesLoading(false);
            setNoticesError(null);
            return;
        }

        let cancelled = false;

        (async () => {
            try {
                const res = await apiGet("/api/notifications/visible");
                const json = await res.json().catch(() => null);
                if (cancelled) return;
                if (!res.ok) {
                    setNotices([]);
                    setNoticesError(
                        typeof json?.error === "string"
                            ? json.error
                            : `notifications_api_error_${res.status}`
                    );
                    return;
                }
                setNotices(Array.isArray(json) ? json : []);
                setNoticesError(null);
            } catch {
                if (cancelled) return;
                setNotices([]);
                setNoticesError("notifications_api_failed");
            } finally {
                if (cancelled) return;
                setNoticesLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [user.loading, user.companyId, isDemo]);

    /* =========================
     * 月次データ取得：KPI（compareYm依存）
     * - KPIだけは compareYm で取り直す
     * - Chart は別Effectで一回だけ取る
     * ========================= */
    useEffect(() => {
        if (user.loading) return;

        if (!isDemo && !user.companyId) {
            setKpiData(null);
            setLoadingKpi(false);
            return;
        }

        let cancelled = false;
        const mySeq = ++reqSeqRef.current;

        if (isDemo) {
            (async () => {
                setLoadingKpi(true);
                setLoadingChart(true);
                try {
                    const json = await fetchJson<MonthlyFullResponse>(
                        `/api/dashboard/monthly-full?compareYm=${encodeURIComponent(compareYm)}`
                    );
                    if (cancelled || mySeq !== reqSeqRef.current) return;

                    setKpiData({
                        anchor_day: json.anchor_day,
                        current: json.current,
                        compare: json.compare,
                        diff: json.diff,
                    });

                    setChartDataRes({
                        anchor_day: json.anchor_day,
                        chart24: json.chart24,
                    });
                    chartLoadedRef.current = !!(json.chart24 && json.chart24.length > 0);
                } catch (e) {
                    console.error(e);
                    if (cancelled || mySeq !== reqSeqRef.current) return;
                    setKpiData(null);
                    setChartDataRes(null);
                    chartLoadedRef.current = false;
                } finally {
                    if (cancelled || mySeq !== reqSeqRef.current) return;
                    setLoadingKpi(false);
                    setLoadingChart(false);
                }
            })();

            return () => {
                cancelled = true;
            };
        }

        (async () => {
            setLoadingKpi(true);
            try {
                const kpi = await fetchJson<MonthlyKpiResponse>(
                    `/api/dashboard/monthly-full?include=kpi&compareYm=${encodeURIComponent(compareYm)}`
                );
                if (cancelled || mySeq !== reqSeqRef.current) return;
                setKpiData(kpi);
            } catch (e) {
                console.error(e);
                if (cancelled || mySeq !== reqSeqRef.current) return;

                try {
                    const json = await fetchJson<MonthlyFullResponse>(
                        `/api/dashboard/monthly-full?compareYm=${encodeURIComponent(compareYm)}`
                    );
                    if (cancelled || mySeq !== reqSeqRef.current) return;

                    setKpiData({
                        anchor_day: json.anchor_day,
                        current: json.current,
                        compare: json.compare,
                        diff: json.diff,
                    });

                    if (Array.isArray(json.chart24)) {
                        setChartDataRes({
                            anchor_day: json.anchor_day,
                            chart24: json.chart24,
                        });
                        chartLoadedRef.current = json.chart24.length > 0;
                        setLoadingChart(false);
                    }
                } catch (e2) {
                    console.error(e2);
                    if (cancelled || mySeq !== reqSeqRef.current) return;
                    setKpiData(null);
                }
            } finally {
                if (cancelled || mySeq !== reqSeqRef.current) return;
                setLoadingKpi(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [user.loading, user.companyId, compareYm, isDemo]);

    /* =========================
     * 月次データ取得：Chart（基本1回だけ）
     * ========================= */
    useEffect(() => {
        if (user.loading) return;

        if (isDemo) return;

        if (!user.companyId) {
            setChartDataRes(null);
            setLoadingChart(false);
            chartLoadedRef.current = false;
            return;
        }

        if (chartLoadedRef.current) {
            setLoadingChart(false);
            return;
        }

        let cancelled = false;

        (async () => {
            setLoadingChart(true);
            try {
                const chart = await fetchJson<MonthlyChartResponse>(
                    `/api/dashboard/monthly-full?include=chart&months=24`
                );
                if (cancelled) return;

                if (chartLoadedRef.current) return;

                setChartDataRes(chart);
                chartLoadedRef.current = !!(chart.chart24 && chart.chart24.length > 0);
            } catch (e) {
                console.error(e);
                if (cancelled) return;
                setChartDataRes((prev) => prev ?? {
                    anchor_day: dayjs().subtract(1, "day").date(),
                    chart24: [],
                });
                chartLoadedRef.current = false;
            } finally {
                if (cancelled) return;
                setLoadingChart(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [user.loading, user.companyId, isDemo]);

    /* =========================
     * 利益構造用：月次表と同じソースを取得
     * ========================= */
    useEffect(() => {
        if (user.loading) return;

        if (!isDemo && !user.companyId) {
            setMonthlyPlRes(null);
            setLoadingMonthlyPl(false);
            return;
        }

        let cancelled = false;

        (async () => {
            setLoadingMonthlyPl(true);
            try {
                const res = await apiGet("/api/dashboard/monthly?months=13");
                const json = await res.json();
                if (cancelled) return;
                setMonthlyPlRes(json);
            } catch (e) {
                console.error(e);
                if (cancelled) return;
                setMonthlyPlRes(null);
            } finally {
                if (cancelled) return;
                setLoadingMonthlyPl(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [user.loading, user.companyId, isDemo]);

    if (user.loading) return <LoadingInline />;

    const anchorDay =
        kpiData?.anchor_day ??
        chartDataRes?.anchor_day ??
        dayjs().subtract(1, "day").date();

    const current = kpiData?.current ?? null;
    const compare = kpiData?.compare ?? current ?? null;

    // chart24 は常に配列化 + 正規化
    const chart24raw = Array.isArray(chartDataRes?.chart24) ? (chartDataRes?.chart24 ?? []) : [];
    const chart24 = chart24raw.filter(
        (x): x is FullKpiSnapshot => !!x && typeof (x as any).year_month === "string"
    );

    const chartData: MonthlySummary[] = chart24.map((r) => ({
        month: String(r.year_month || "").replace(/-01$/, "").replace(/-/g, "/"),
        sales: Number((r as any).sales ?? 0) || 0,
        total_advertising: Number((r as any).total_advertising ?? 0) || 0,
        visit: Number(r.visit ?? 0) || 0,
    }));

    const chart24sorted = [...chart24].sort((a, b) =>
        String(a.year_month).localeCompare(String(b.year_month))
    );

    const ymKey = (v: any) => {
        const d = dayjs(v);
        if (d.isValid()) return d.format("YYYY-MM");

        const s = String(v ?? "").trim();
        if (/^\d{4}-\d{2}/.test(s)) return s.slice(0, 7);
        return s;
    };

    // =========================
    // 広告費内訳（8項目） 前期比較用データ
    // =========================
    const adBreakdownYoyData: AdCostBreakdownYoYRow[] = (() => {
        if (isNonRakutenMall) return [];
        if (!chart24sorted.length) return [];

        const last13 = chart24sorted.slice(-13);
        const byYm = new Map(chart24sorted.map((r) => [String(r.year_month).slice(0, 7), r as any]));

        const num = (v: any) => Number(v ?? 0) || 0;

        return last13.map((r: any) => {
            const ym = String(r.year_month).slice(0, 7);
            const lyYm = dayjs(ym + "-01").subtract(1, "year").format("YYYY-MM");
            const ly = byYm.get(lyYm) as any | undefined;

            return {
                month: ym.replace(/-/g, "/"),

                rakuten_prev: num(ly?.ec_amount_spent),
                rpp_prev: num(ly?.rpp_amount_spent),
                rppexp_prev: num(ly?.rppexp_amount_spent),
                cpa_prev: num(ly?.cpa_amount_spent),
                cpnadv_prev: num(ly?.cpnadv_amount_spent),
                tda_prev: num(ly?.tda_amount_spent),
                tdaexp_prev: num(ly?.tdaexp_amount_spent),
                deal_prev: num(ly?.deal_amount_spent),

                rakuten_cur: num(r.ec_amount_spent),
                rpp_cur: num(r.rpp_amount_spent),
                rppexp_cur: num(r.rppexp_amount_spent),
                cpa_cur: num(r.cpa_amount_spent),
                cpnadv_cur: num(r.cpnadv_amount_spent),
                tda_cur: num(r.tda_amount_spent),
                tdaexp_cur: num(r.tdaexp_amount_spent),
                deal_cur: num(r.deal_amount_spent),
            };
        });
    })();

    // =========================
    // 利益構造：/api/dashboard/monthly を利用
    // =========================
    const profitStructureYoyData: ProfitStructureBreakdownYoYRow[] = (() => {
        const monthlyRows = monthlyPlRes?.data;
        const rows = Array.isArray(monthlyRows) ? monthlyRows : [];
        if (!rows.length) return [];

        const sorted = [...rows].sort((a, b) =>
            ymKey(a.year_month).localeCompare(ymKey(b.year_month))
        );

        const last13 = sorted.slice(-13);

        const byYm = new Map(
            sorted.map((r) => [ymKey(r.year_month), r])
        );

        const num = (v: any) => Number(v ?? 0) || 0;

        const getOperating = (row: any) => {
            if (!row) return 0;
            const op = row.operating_profit;
            if (op !== null && op !== undefined && op !== "") return num(op);
            return num(row.marginal_profit) - num(row.fixed_cost);
        };

        return last13.map((r) => {
            const ym = ymKey(r.year_month);
            const lyYm = dayjs(`${ym}-01`).subtract(1, "year").format("YYYY-MM");
            const ly = byYm.get(lyYm) as any | undefined;

            return {
                month: dayjs(`${ym}-01`).format("YYYY/MM"),

                sales_prev: num(ly?.sales),
                cost_prev: num(ly?.cost_amount),
                fee_prev: num(ly?.fee),
                logistics_prev: num(ly?.logistics_cost),
                promotion_prev: num(ly?.total_advertising),
                marginalProfit_prev: num(ly?.marginal_profit),
                fixedCost_prev: num(ly?.fixed_cost),
                operatingProfit_prev: getOperating(ly),

                sales_cur: num(r.sales),
                cost_cur: num(r.cost_amount),
                fee_cur: num(r.fee),
                logistics_cur: num(r.logistics_cost),
                promotion_cur: num(r.total_advertising),
                marginalProfit_cur: num(r.marginal_profit),
                fixedCost_cur: num(r.fixed_cost),
                operatingProfit_cur: getOperating(r),
            };
        });
    })();

    // =========================
    // 売上・限界利益・営業利益 前年同月比較
    // =========================
    const profitYoyData: MonthlySalesProfitYoYRow[] = isSuperUser
        ? (() => {
            const last13 = chart24sorted.slice(-13);
            const byYm = new Map(chart24sorted.map((r) => [String(r.year_month).slice(0, 7), r]));

            const getMarginal = (r: any) => Number(r?.marginal_profit ?? 0) || 0;
            const getOperating = (r: any) => Number(r?.operating_profit ?? 0) || 0;
            const yoy = (cur: number, prev: number) => (prev > 0 ? cur / prev - 1 : null);

            return last13.map((r) => {
                const ym = String(r.year_month).slice(0, 7);
                const lyYm = dayjs(ym + "-01").subtract(1, "year").format("YYYY-MM");
                const ly = byYm.get(lyYm) as any | undefined;

                const sales = Number((r as any).sales ?? 0) || 0;
                const mp = getMarginal(r as any);
                const op = getOperating(r as any);

                const lySales = ly ? (Number((ly as any).sales ?? 0) || 0) : 0;
                const lyMp = ly ? getMarginal(ly) : 0;
                const lyOp = ly ? getOperating(ly) : 0;

                return {
                    month: ym.replace(/-/g, "/"),
                    sales,
                    marginalProfit: mp,
                    operatingProfit: op,
                    salesYoy: yoy(sales, lySales),
                    marginalProfitYoy: yoy(mp, lyMp),
                    operatingProfitYoy: yoy(op, lyOp),
                };
            });
        })()
        : [];

    // =========================
    // 販促費内訳（13か月）
    // =========================
    const promoBreakdownYoyData: PromotionCostBreakdownYoYRow[] = (() => {
        if (!chart24sorted.length) return [];

        const last13 = chart24sorted.slice(-13);
        const byYm = new Map(chart24sorted.map((r) => [String(r.year_month).slice(0, 7), r]));
        const pick = (obj: any, key: string) => Number(obj?.[key] ?? 0) || 0;

        return last13.map((r) => {
            const ym = String(r.year_month).slice(0, 7);
            const lyYm = dayjs(ym + "-01").subtract(1, "year").format("YYYY-MM");
            const ly = byYm.get(lyYm) as any | undefined;

            return {
                month: ym.replace(/-/g, "/"),

                point_prev: pick(ly, "point_amount_spent"),
                mail_prev: pick(ly, "mail_amount_spent"),
                affiliate_prev: pick(ly, "afl_amount_spent"),
                ads_prev: pick(ly, "total_promotion"),

                point_cur: pick(r as any, "point_amount_spent"),
                mail_cur: pick(r as any, "mail_amount_spent"),
                affiliate_cur: pick(r as any, "afl_amount_spent"),
                ads_cur: pick(r as any, "total_promotion"),
            };
        });
    })();

    const baseYm =
        (current?.year_month ? current.year_month.slice(0, 7) : "") ||
        (chart24.length > 0 ? chart24[chart24.length - 1].year_month.slice(0, 7) : "") ||
        dayjs().subtract(1, "day").format("YYYY-MM");

    const monthOptions = generateMonthOptions(baseYm, anchorDay);
    const monthOptionsSafe =
        monthOptions.length > 0
            ? monthOptions
            : [
                {
                    value: compareYm,
                    label: dayjs(compareYm + "-01").format("YYYY年MM月"),
                },
            ];

    const compareMonthLabel = compareYm
        ? dayjs(compareYm + "-01").format("YYYY年MM月")
        : "—";

    const comparePeriodLabel =
        compare?.period_start && compare?.period_end
            ? `${compareMonthLabel}（${compare.period_start}〜${compare.period_end}）`
            : `${compareMonthLabel}`;

    const curYmForLabel = (current?.year_month ?? `${baseYm}-01`).slice(0, 7);
    const currentMonthLabel = dayjs(curYmForLabel + "-01").format("YYYY年MM月");
    const currentPeriodLabel =
        current?.period_start && current?.period_end
            ? `${currentMonthLabel}（${current.period_start}〜${current.period_end}）`
            : `${currentMonthLabel}`;

    const kpiReady = !!current?.year_month;

    return (
        <div className="w-pl flex flex-col">
            {!!noticesError && (
                <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                    お知らせの取得でエラーが発生しました: {noticesError}
                </div>
            )}

            {!noticesLoading && notices.length > 0 && (
                <div className="mb-8 space-y-4">
                    {notices.map((n) => {
                        const meta = noticeMeta(n.level);
                        return (
                            <div
                                key={n.id}
                                className="relative bg-white rounded-xl border shadow-sm overflow-hidden"
                            >
                                <div className={`absolute left-0 top-0 h-full w-1 ${meta.badge}`} />
                                <div className="pl-5 pr-4 py-4 space-y-2">
                                    <div className="flex items-center gap-2 text-xs text-gray-500">
                                        <span className={`px-2 py-0.5 rounded text-white ${meta.badge}`}>
                                            {meta.label}
                                        </span>
                                        <span>運営からのお知らせ</span>
                                    </div>

                                    <div className="text-sm font-semibold text-gray-900">{n.title}</div>

                                    <div
                                        className="text-sm text-gray-700 leading-relaxed prose prose-sm max-w-none"
                                        dangerouslySetInnerHTML={{
                                            __html: DOMPurify.sanitize(n.bodyHtml ?? n.body ?? ""),
                                        }}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <div className="flex justify-between items-end mb-4 gap-3">
                <div className="flex items-center gap-2">
                    <KpiVisibilitySettings visible={visibleKpis} onChange={setVisibleKpis} />
                    <span className="text-sm text-gray-600">
                        上段：当月の実績 ／ 下段：比較対象月の実績と増減率
                    </span>
                </div>

                <div className="flex flex-col items-end">
                    <div className="flex items-center gap-0">
                        <span className="text-sm text-gray-600">
                            比較対象月を選択する：
                        </span>
                        <select
                            className={`border rounded px-2 py-1 text-sm ${isFreePlan ? "cursor-not-allowed opacity-80" : ""
                                }`}
                            value={compareYm}
                            onMouseDown={(e) => {
                                if (!isFreePlan) return;
                                e.preventDefault();
                                e.stopPropagation();
                                showPlanBlocked();
                            }}
                            onTouchStart={(e) => {
                                if (!isFreePlan) return;
                                e.preventDefault();
                                e.stopPropagation();
                                showPlanBlocked();
                            }}
                            onKeyDown={(e) => {
                                if (!isFreePlan) return;
                                if (["Enter", " ", "ArrowUp", "ArrowDown"].includes(e.key)) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    showPlanBlocked();
                                }
                            }}
                            onChange={(e) => {
                                if (isFreePlan) {
                                    showPlanBlocked();
                                    return;
                                }
                                setLoadingKpi(true);
                                setCompareYm(e.target.value);
                            }}
                        >
                            {monthOptionsSafe.map((m) => (
                                <option key={m.value} value={m.value}>
                                    {m.label}
                                </option>
                            ))}
                        </select>

                        <HelpTip
                            className="ml-2"
                            placement="right"
                            text={
                                <>
                                    ここで選択した月は、各KPIの下段に表示されている「比較データ」に反映されます。<br />
                                    変更すると、すべてのKPIの下段数値が同時に切り替わります。
                                    {isFreePlan ? (
                                        <>
                                            <br />
                                            ※フリープランでは切替できません。
                                        </>
                                    ) : null}
                                </>
                            }
                        />
                    </div>

                    {planBlockMsg ? (
                        <div className="mt-1 text-xs text-red-600">{planBlockMsg}</div>
                    ) : loadingKpi ? (
                        <div className="mt-1 flex items-center gap-2 text-xs text-slate-500">
                            <InlineSpinner />
                            <span>更新中...</span>
                        </div>
                    ) : null}
                </div>
            </div>

            <div className="flex flex-col gap-6">
                {kpiReady || loadingKpi ? (
                    <div className="relative">
                        <KpiCards
                            current={current}
                            compare={compare}
                            visible={visibleKpisForMall}
                            loading={loadingKpi && !kpiReady}
                        />

                        {kpiReady && loadingKpi && (
                            <div className="absolute inset-0 rounded-2xl bg-white/60 backdrop-blur-[1px] flex items-center justify-center">
                                <div className="flex items-center gap-2 text-sm text-slate-600">
                                    <InlineSpinner />
                                    <span>更新中...</span>
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="rounded-2xl border bg-white p-4 text-sm text-slate-500">
                        KPIデータがありません
                    </div>
                )}

                {/* 初期設定・面談バナーはKPIの下に配置（利益の数字を最上部に優先表示） */}
                <SetupBanner plan={user.plan} tenantId={user.tenantId} companyId={user.companyId} />
                <ZoomBanner plan={user.plan} companyId={user.companyId} />

                <MonthlyChart
                    data={chartData}
                    loading={false}
                    emptyLabel={loadingChart ? "読み込み中..." : "データがありません"}
                />

                <MonthlySalesProfitYoYChart
                    data={profitYoyData}
                    loading={false}
                    emptyLabel={loadingChart ? "読み込み中..." : "データがありません"}
                />

                <PromotionCostBreakdownYoYChart
                    data={promoBreakdownYoyData}
                    loading={false}
                    emptyLabel={loadingChart ? "読み込み中..." : "データがありません"}
                    title={
                        isAmazonMall
                            ? "販促費内訳（ポイント・広告費）"
                            : isYahooMall
                              ? "販促費内訳（販促・広告費）"
                              : "販促費内訳（ポイント・メルマガ・アフィリ・広告費）"
                    }
                    showCompare={promoShowCompare}
                    onChangeShowCompare={setPromoShowCompare}
                    compareLabel="前期比較（前年同月）"
                />

                <ProfitStructureYoYChart
                    data={profitStructureYoyData}
                    loading={false}
                    emptyLabel={loadingMonthlyPl ? "読み込み中..." : "データがありません"}
                    showCompare={profitStructShowCompare}
                    onChangeShowCompare={setProfitStructShowCompare}
                    compareLabel="前期比較（前年同月）"
                />

                {!isNonRakutenMall && (
                    <>
                        <AdCostBreakdownYoYChart
                            data={adBreakdownYoyData}
                            loading={false}
                            emptyLabel={loadingChart ? "読み込み中..." : "データがありません"}
                            showCompare={adShowCompare}
                            onChangeShowCompare={setAdShowCompare}
                        />

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <AdPieChart
                                data={kpiReady ? toAdPieData(compare) : []}
                                periodLabel={comparePeriodLabel}
                                loading={false}
                                emptyLabel={loadingKpi ? "読み込み中..." : "データがありません"}
                            />

                            <AdPieChart
                                data={kpiReady ? toAdPieData(current) : []}
                                periodLabel={currentPeriodLabel}
                                loading={false}
                                emptyLabel={loadingKpi ? "読み込み中..." : "データがありません"}
                            />
                        </div>
                    </>
                )}

                {!loadingKpi && !loadingChart && !kpiData && !chartDataRes && (
                    <div className="rounded-2xl border bg-white p-4 text-sm text-slate-600">
                        データを取得できませんでした。
                    </div>
                )}
            </div>
        </div>
    );
};

function generateMonthOptions(
    currentYm: string,
    anchorDay: number
): { value: string; label: string }[] {
    const base = dayjs(currentYm + "-01");
    const start = anchorDay === 1 ? base.subtract(2, "month") : base.subtract(1, "month");

    return Array.from({ length: 24 }).map((_, i) => {
        const m = start.subtract(i, "month");
        return {
            value: m.format("YYYY-MM"),
            label: m.format("YYYY年MM月"),
        };
    });
}

export default DashboardPage;